import { useEffect, useMemo, useRef, useState } from 'react'
import { fetchHistoricalCandles } from '../services/candle-service'
import {
    calculateIntradayLevels,
    calculateRSI,
    aggregate1mTo5m,
    type IndicatorLevels,
} from '../lib/technical-indicators'
import type { MarketData } from '../types'

interface CachedStrikeIndicator {
    ind: IndicatorLevels | undefined
    min5: any[]
    fetchedAt: number
}

// Module-level global cache and in-flight deduplicator for strike indicators
const GLOBAL_STRIKE_INDICATORS_CACHE = new Map<string, CachedStrikeIndicator>()
const IN_FLIGHT_STRIKE_FETCH = new Map<string, Promise<{ ind: IndicatorLevels | undefined; min5: any[] }>>()

interface UseStrikeIndicatorsProps {
    selectedCeStrike: number
    selectedPeStrike: number
    activeMarketData: MarketData | null
    symbol: string
    historicalDate?: string | null
}

export function useStrikeIndicators({
    selectedCeStrike,
    selectedPeStrike,
    activeMarketData,
    symbol,
    historicalDate,
}: UseStrikeIndicatorsProps) {
    const [ceIndicators, setCeIndicators] = useState<IndicatorLevels | undefined>()
    const [peIndicators, setPeIndicators] = useState<IndicatorLevels | undefined>()
    const [ceMinute5Data, setCeMinute5Data] = useState<any[]>([])
    const [peMinute5Data, setPeMinute5Data] = useState<any[]>([])
    const [loading, setLoading] = useState(false)

    // ── Extract instrument keys ──
    const { ceKey, peKey } = useMemo(() => {
        const options = activeMarketData?.options || (activeMarketData as any)?.optionChain || []
        if (!options || options.length === 0) return { ceKey: '', peKey: '' }

        const ceOption = options.find((o: any) => o.strike === selectedCeStrike || o.strikePrice === selectedCeStrike)
        const peOption = options.find((o: any) => o.strike === selectedPeStrike || o.strikePrice === selectedPeStrike)

        let ceKey =
            (ceOption as any)?.ceInstrumentKey ||
            (ceOption as any)?.callInstrumentKey ||
            (ceOption as any)?.ce?.instrumentKey ||
            ''
        let peKey =
            (peOption as any)?.peInstrumentKey ||
            (peOption as any)?.putInstrumentKey ||
            (peOption as any)?.pe?.instrumentKey ||
            ''

        if (!ceKey && (ceOption as any)?.callOptionData) ceKey = (ceOption as any).callOptionData.instrumentKey || ''
        if (!peKey && (peOption as any)?.putOptionData) peKey = (peOption as any).putOptionData.instrumentKey || ''

        return { ceKey, peKey }
    }, [activeMarketData, selectedCeStrike, selectedPeStrike])

    // Update real-time RSI when activeMarketData changes
    // Using refs to prevent state-setting cascades that cause UI hangs
    const ceIndicatorsRef = useRef(ceIndicators);
    ceIndicatorsRef.current = ceIndicators;
    const peIndicatorsRef = useRef(peIndicators);
    peIndicatorsRef.current = peIndicators;
    const ceMinute5DataRef = useRef(ceMinute5Data);
    ceMinute5DataRef.current = ceMinute5Data;
    const peMinute5DataRef = useRef(peMinute5Data);
    peMinute5DataRef.current = peMinute5Data;
    const rsiUpdateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        // Debounce RSI updates to prevent cascading re-renders when switching tabs
        if (rsiUpdateTimer.current) clearTimeout(rsiUpdateTimer.current);
        rsiUpdateTimer.current = setTimeout(() => {
            const updateLiveRsi = (ind: IndicatorLevels | undefined, min5Data: any[], strike: number, isCe: boolean) => {
                if (!ind || min5Data.length === 0) return ind;
                const options = activeMarketData?.options || (activeMarketData as any)?.optionChain || [];
                const option = options.find((o: any) => o.strike === strike || o.strikePrice === strike);
                
                const liveLtp = isCe 
                    ? (option?.callPrice ?? option?.callOption?.ltp ?? option?.callOptionData?.ltp ?? option?.ce?.ltp)
                    : (option?.putPrice ?? option?.putOption?.ltp ?? option?.putOptionData?.ltp ?? option?.pe?.ltp);

                if (liveLtp) {
                    const clonedData = [...min5Data];
                    const lastCandle = { ...clonedData[clonedData.length - 1], close: liveLtp };
                    clonedData[clonedData.length - 1] = lastCandle;
                    
                    const liveRsi = calculateRSI(clonedData, 14);
                    if (ind.rsi5m !== liveRsi) {
                        return { ...ind, rsi5m: liveRsi };
                    }
                }
                return ind;
            };

            const ceData = ceMinute5DataRef.current;
            const peData = peMinute5DataRef.current;
            const ceInd = ceIndicatorsRef.current;
            const peInd = peIndicatorsRef.current;

            if (ceData.length > 0 && ceInd) {
                const updated = updateLiveRsi(ceInd, ceData, selectedCeStrike, true);
                if (updated !== ceInd) setCeIndicators(updated);
            }
            if (peData.length > 0 && peInd) {
                const updated = updateLiveRsi(peInd, peData, selectedPeStrike, false);
                if (updated !== peInd) setPeIndicators(updated);
            }
        }, 100); // 100ms debounce to batch with other state changes

        return () => { if (rsiUpdateTimer.current) clearTimeout(rsiUpdateTimer.current); };
    }, [activeMarketData, selectedCeStrike, selectedPeStrike])

    // Guard against duplicate in-flight fetches
    const fetchIdRef = useRef(0)

    useEffect(() => {
        const currentFetchId = ++fetchIdRef.current

        const loadIndicators = async () => {
            if (!symbol) return
            if (!selectedCeStrike || !selectedPeStrike) return
            if (!ceKey && !peKey) return

            setLoading(true)

            try {
                const fetchForInstrument = async (instrumentKey: string): Promise<{ ind: IndicatorLevels | undefined, min5: any[] }> => {
                    if (!instrumentKey || instrumentKey === '') return { ind: undefined, min5: [] }

                    const cacheKey = `${instrumentKey}:${historicalDate || 'live'}`
                    const cached = GLOBAL_STRIKE_INDICATORS_CACHE.get(cacheKey)
                    const ttl = historicalDate ? 30 * 60 * 1000 : 45 * 1000
                    if (cached && Date.now() - cached.fetchedAt < ttl) {
                        return cached
                    }

                    if (IN_FLIGHT_STRIKE_FETCH.has(cacheKey)) {
                        return IN_FLIGHT_STRIKE_FETCH.get(cacheKey)!
                    }

                    const fetchPromise = (async () => {
                        const isOptionKey = instrumentKey.includes('_FO|') || (instrumentKey.includes('|') && !instrumentKey.includes('_INDEX'))

                        let dailyData: any[] = []
                        let monthlyData: any[] = []
                        let weeklyData: any[] = []
                        let minuteData: any[] = []
                        let minute5Data: any[] = []

                        if (isOptionKey) {
                            // For options contracts: they only exist for current expiry series (days/weeks).
                            // Month/week historical endpoints from Upstox return errors or empty data for options.
                            // 5minute is aggregated from 1minute.
                            const results = await Promise.allSettled([
                                fetchHistoricalCandles(symbol, 60, 'day', instrumentKey, historicalDate),
                                fetchHistoricalCandles(symbol, 1, '1minute', instrumentKey, historicalDate),
                            ])
                            dailyData = results[0].status === 'fulfilled' ? results[0].value : []
                            minuteData = results[1].status === 'fulfilled' ? results[1].value : []
                            minute5Data = aggregate1mTo5m(minuteData)
                        } else {
                            // Index instruments
                            const results = await Promise.allSettled([
                                fetchHistoricalCandles(symbol, 60, 'day', instrumentKey, historicalDate),
                                fetchHistoricalCandles(symbol, 180, 'month', instrumentKey, historicalDate),
                                fetchHistoricalCandles(symbol, 180, 'week', instrumentKey, historicalDate),
                                fetchHistoricalCandles(symbol, 1, '1minute', instrumentKey, historicalDate),
                                fetchHistoricalCandles(symbol, 5, '5minute', instrumentKey, historicalDate),
                            ])
                            dailyData = results[0].status === 'fulfilled' ? results[0].value : []
                            monthlyData = results[1].status === 'fulfilled' ? results[1].value : []
                            weeklyData = results[2].status === 'fulfilled' ? results[2].value : []
                            minuteData = results[3].status === 'fulfilled' ? results[3].value : []
                            minute5Data = results[4].status === 'fulfilled' ? results[4].value : []

                            if ((!minute5Data || minute5Data.length === 0) && minuteData && minuteData.length > 0) {
                                minute5Data = aggregate1mTo5m(minuteData)
                            }
                        }

                        const todayStr = historicalDate ? new Date(historicalDate).toDateString() : new Date().toDateString()

                        let prevDay = null
                        if (dailyData.length > 0) {
                            const lastDay = dailyData[dailyData.length - 1]
                            if (lastDay) {
                                const isToday = new Date(lastDay.timestamp).toDateString() === todayStr
                                const prevDayRaw = isToday
                                    ? dailyData.length > 1
                                        ? dailyData[dailyData.length - 2]
                                        : lastDay
                                    : lastDay
                                if (prevDayRaw)
                                    prevDay = { high: prevDayRaw.high, low: prevDayRaw.low, close: prevDayRaw.close }
                            }
                        }

                        let prevMonth = null
                        if (monthlyData.length > 0) {
                            const lastMonth = monthlyData[monthlyData.length - 1]
                            if (lastMonth) {
                                const isCurrentMonth =
                                    new Date(lastMonth.timestamp).getMonth() === new Date(todayStr).getMonth() &&
                                    new Date(lastMonth.timestamp).getFullYear() === new Date(todayStr).getFullYear()
                                const prevMonthRaw = isCurrentMonth
                                    ? monthlyData.length > 1
                                        ? monthlyData[monthlyData.length - 2]
                                        : lastMonth
                                    : lastMonth
                                if (prevMonthRaw)
                                    prevMonth = {
                                        high: prevMonthRaw.high,
                                        low: prevMonthRaw.low,
                                        close: prevMonthRaw.close,
                                    }
                            }
                        } else if (weeklyData.length > 0) {
                            // Fallback to absolute lowest week if month data missing
                            const oldestWeek = weeklyData[0]
                            if (oldestWeek)
                                prevMonth = { high: oldestWeek.high, low: oldestWeek.low, close: oldestWeek.close }
                        } else if (dailyData.length > 0) {
                            const oldestDay = dailyData[0]
                            if (oldestDay) prevMonth = { high: oldestDay.high, low: oldestDay.low, close: oldestDay.close }
                        }

                        let prevWeek = null
                        if (weeklyData.length > 0) {
                            const lastWeek = weeklyData[weeklyData.length - 1]
                            if (lastWeek) {
                                const daysDiff =
                                    (new Date(todayStr).getTime() - new Date(lastWeek.timestamp).getTime()) / (1000 * 3600 * 24)
                                const prevWeekRaw =
                                    daysDiff < 7
                                        ? weeklyData.length > 1
                                            ? weeklyData[weeklyData.length - 2]
                                            : lastWeek
                                        : lastWeek
                                if (prevWeekRaw)
                                    prevWeek = { high: prevWeekRaw.high, low: prevWeekRaw.low, close: prevWeekRaw.close }
                            }
                        } else if (dailyData.length > 0) {
                            // Fallback to oldest daily data if week missing
                            const oldestDay = dailyData[0]
                            if (oldestDay) prevWeek = { high: oldestDay.high, low: oldestDay.low, close: oldestDay.close }
                        }

                        let prev2Months = null
                        if (dailyData.length > 0) {
                            const last60 = dailyData.slice(-60)
                            if (last60.length > 0) {
                                const high = Math.max(...last60.map((d: any) => d.high))
                                const low = Math.min(...last60.map((d: any) => d.low))
                                const close = last60[last60.length - 1]?.close ?? 0
                                prev2Months = { high, low, close }
                            }
                        }

                        const todayCandles = minuteData.filter(
                            (c: any) => new Date(c.timestamp).toDateString() === todayStr,
                        )
                        const intradayLevels = calculateIntradayLevels(todayCandles)

                        const rsi5m = calculateRSI(minute5Data, 14)

                        const result = {
                            ind: {
                                prev2Months,
                                prevDay,
                                prevWeek,
                                prevMonth,
                                first5Min: intradayLevels.first5Min,
                                first15Min: intradayLevels.first15Min,
                                rsi5m,
                            },
                            min5: minute5Data,
                        }

                        GLOBAL_STRIKE_INDICATORS_CACHE.set(cacheKey, { ...result, fetchedAt: Date.now() })
                        return result
                    })().catch((err) => {
                        console.warn(`[useStrikeIndicators] Error loading indicators for ${instrumentKey}:`, err)
                        if (cached) return cached
                        return { ind: undefined, min5: [] }
                    }).finally(() => {
                        IN_FLIGHT_STRIKE_FETCH.delete(cacheKey)
                    })

                    IN_FLIGHT_STRIKE_FETCH.set(cacheKey, fetchPromise)
                    return fetchPromise
                }

                const [ceRes, peRes] = await Promise.all([fetchForInstrument(ceKey), fetchForInstrument(peKey)])

                // Only update state if this is still the latest fetch
                if (currentFetchId === fetchIdRef.current) {
                    setCeIndicators(ceRes.ind)
                    setPeIndicators(peRes.ind)
                    setCeMinute5Data(ceRes.min5)
                    setPeMinute5Data(peRes.min5)

                    // ── Cache results in IDB ──
                    try {
                        const { HistoryCache } = await import('../lib/idb-cache')
                        if (ceRes.ind)
                            await HistoryCache.set(`indicators_ce_${symbol}_${selectedCeStrike}`, [ceRes.ind] as any, '')
                        if (peRes.ind)
                            await HistoryCache.set(`indicators_pe_${symbol}_${selectedPeStrike}`, [peRes.ind] as any, '')
                    } catch (e) {
                        /* ignore */
                    }
                }
            } catch (e) {
                console.error('[useStrikeIndicators] Failed to load strike indicators:', e)
            } finally {
                if (currentFetchId === fetchIdRef.current) {
                    setLoading(false)
                }
            }
        }

        loadIndicators()
    }, [selectedCeStrike, selectedPeStrike, ceKey, peKey, symbol, historicalDate])

    return { ceIndicators, peIndicators, loading, debugKeys: { ceKey, peKey } }
}
